export function formatAmount(value, currency = 'EUR', locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value ?? 0)
  } catch {
    return `${(value ?? 0).toFixed(2)} ${currency}`
  }
}

// ── Timezone-stable "wall-clock" handling ──────────────────────────────────
// Times must always show as when the expense happened, independent of the
// viewer's current timezone. New transactions store a NAIVE wall-clock string
// `YYYY-MM-DDTHH:mm` (no zone). Legacy transactions (created before this fix)
// stored a UTC instant anchored to the device timezone at creation — the
// user's home base, Albania — so we reinterpret those in Albanian local time
// to reproduce exactly what was shown there. Editing a legacy row re-saves it
// naive, migrating it for free.
const pad2 = (n) => String(n).padStart(2, '0')

function hasZone(s) { return /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s) }

// Last Sunday of `month` (0-based) at 01:00 UTC — the EU DST switch instant.
function lastSundayUTC(year, month) {
  const d = new Date(Date.UTC(year, month + 1, 0, 1, 0, 0)) // last day of month, 01:00 UTC
  d.setUTCDate(d.getUTCDate() - d.getUTCDay())              // back to Sunday
  return d.getTime()
}

// Albania (CET) UTC offset in minutes for a given instant. EU DST: +120 from
// last Sunday of March 01:00 UTC to last Sunday of October 01:00 UTC, else +60.
// Computed manually so we never depend on Intl timezone data (some devices
// ship a reduced ICU that only knows UTC and throws on named zones).
function albaniaOffsetMin(dt) {
  const y = dt.getUTCFullYear()
  const t = dt.getTime()
  const start = lastSundayUTC(y, 2)  // March
  const end   = lastSundayUTC(y, 9)  // October
  return (t >= start && t < end) ? 120 : 60
}

// Returns { y, m, d, hh, mm } wall-clock components for either date format,
// or null if unparseable.
export function wallClock(iso) {
  if (!iso) return null
  const s = String(iso)
  const tIdx = s.indexOf('T')
  if (tIdx < 0) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number)
    if (!y) return null
    return { y, m, d, hh: 0, mm: 0 }
  }
  if (!hasZone(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number)
    return { y, m, d, hh: Number(s.slice(11, 13)) || 0, mm: Number(s.slice(14, 16)) || 0 }
  }
  // Legacy zoned/UTC instant → wall-clock in the historical home zone (Albania).
  const dt = new Date(s)
  if (Number.isNaN(dt.getTime())) return null
  const local = new Date(dt.getTime() + albaniaOffsetMin(dt) * 60000)
  return {
    y: local.getUTCFullYear(), m: local.getUTCMonth() + 1, d: local.getUTCDate(),
    hh: local.getUTCHours(), mm: local.getUTCMinutes(),
  }
}

// A local Date at noon on the wall-clock day (noon avoids TZ day-flips when the
// object is later re-serialised). For day headers / weekday display.
export function wallClockDate(iso) {
  const w = wallClock(iso)
  return w ? new Date(w.y, w.m - 1, w.d, 12, 0, 0) : null
}

// Sortable string key from wall-clock components (lexicographic == chronological).
export function wallClockKey(iso) {
  const w = wallClock(iso)
  return w ? `${w.y}${pad2(w.m)}${pad2(w.d)}${pad2(w.hh)}${pad2(w.mm)}` : ''
}

// ── Date display order ─────────────────────────────────────────────────────
// 'auto' keeps the locale's own order (what the app did before this setting);
// the others pin it, for people whose language and habits disagree.
export const DATE_FORMATS = ['auto', 'dmy', 'mdy', 'ymd']

export function formatDay(iso, dateFormat = 'auto', locale) {
  const w = wallClock(iso)
  if (!w) return iso ?? ''
  const d = pad2(w.d), m = pad2(w.m), y = w.y
  if (dateFormat === 'dmy') return `${d}/${m}/${y}`
  if (dateFormat === 'mdy') return `${m}/${d}/${y}`
  if (dateFormat === 'ymd') return `${y}-${m}-${d}`
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    return new Intl.DateTimeFormat(lang, {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(w.y, w.m - 1, w.d))
  } catch {
    return `${d}/${m}/${y}`
  }
}

// Short-month style ("16 ott 2026"). An explicit order wins over the style:
// if the user pinned one, every date in the app follows it.
export function formatDate(iso, locale, dateFormat = 'auto') {
  if (dateFormat && dateFormat !== 'auto') return formatDay(iso, dateFormat, locale)
  const lang = locale ?? document.documentElement.lang ?? 'en'
  const w = wallClock(iso)
  if (!w) return iso
  try {
    return new Intl.DateTimeFormat(lang, {
      year: 'numeric', month: 'short', day: '2-digit',
    }).format(new Date(w.y, w.m - 1, w.d))
  } catch {
    return iso
  }
}

export function startOfMonth(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

export function formatTime(iso, locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  const w = wallClock(iso)
  if (!w) return ''
  try {
    return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .format(new Date(w.y, w.m - 1, w.d, w.hh, w.mm))
  } catch {
    return ''
  }
}

export function weekdayShort(d, locale) {
  const lang = locale ?? document.documentElement.lang ?? 'en'
  try {
    // 3-letter abbreviation, uppercased to feel chip-like
    return new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d).slice(0, 3).toUpperCase()
  } catch {
    return ''
  }
}

// Wall-clock YYYY-MM-DD key for grouping (timezone-independent)
export function dayKey(iso) {
  const w = wallClock(iso)
  if (!w) return ''
  return `${w.y}-${pad2(w.m)}-${pad2(w.d)}`
}

// The date used for displaying/grouping a transaction. If the tx has an
// `effectiveDate` (e.g. flight paid in May for a July departure), that wins
// over `date` (which is the payment date, kept for FX conversion).
export function displayDate(tx) {
  return tx?.effectiveDate || tx?.date || null
}

// Valid split units. Mirrors the backend's SPLIT_UNITS set.
export const SPLIT_UNITS = new Set([
  'days', 'weeks',
  'months_greg', 'months_lil',
  'years_greg',  'years_lil',
])

// Add `n` units of `unit` to an ISO datetime. Months/years use proper
// calendar arithmetic (variable-length); lil units use fixed-day
// approximations (28d / 364d) per the project's Lil calendar model.
export function addInterval(isoDate, n, unit) {
  const w = wallClock(isoDate)
  if (!w) return isoDate
  const base = new Date(w.y, w.m - 1, w.d, w.hh, w.mm) // local wall-clock
  // Calendar arithmetic, never elapsed milliseconds: adding n*86400000 ms
  // across a DST change lands an hour off (and so a DAY off once the date is
  // sliced out) — e.g. 16 Oct + 13 days became 28 Oct 23:00 instead of 29 Oct.
  const addDays = (days) => { const d = new Date(base); d.setDate(d.getDate() + days); return d }
  let out
  switch (unit) {
    case 'days':        out = addDays(n); break
    case 'weeks':       out = addDays(n * 7); break
    case 'months_lil':  out = addDays(n * 28); break
    case 'years_lil':   out = addDays(n * 364); break
    case 'months_greg': out = new Date(base); out.setMonth(out.getMonth() + n); break
    case 'years_greg':  out = new Date(base); out.setFullYear(out.getFullYear() + n); break
    default:            out = addDays(n)
  }
  // Naive wall-clock output — no zone, so it never re-shifts on display.
  return `${out.getFullYear()}-${pad2(out.getMonth() + 1)}-${pad2(out.getDate())}T${pad2(out.getHours())}:${pad2(out.getMinutes())}`
}

export const SPLIT_MODES = new Set(['divide', 'repeat'])

// Resolve split fields from a tx, accepting both the current
// {splitInterval, splitUnit} shape and the legacy splitIntervalDays alias.
// splitMode defaults to 'divide' (the original behaviour).
function splitParts(tx) {
  const c = tx?.splitCount
  if (!Number.isInteger(c) || c < 2) return null
  if (!tx?.effectiveDate) return null
  const mode = SPLIT_MODES.has(tx?.splitMode) ? tx.splitMode : 'divide'
  if (Number.isInteger(tx?.splitInterval) && tx.splitInterval >= 1
      && SPLIT_UNITS.has(tx?.splitUnit)) {
    return { count: c, interval: tx.splitInterval, unit: tx.splitUnit, mode }
  }
  if (Number.isInteger(tx?.splitIntervalDays) && tx.splitIntervalDays >= 1) {
    return { count: c, interval: tx.splitIntervalDays, unit: 'days', mode }
  }
  return null
}

// Is this tx configured to split into N virtual slices?
export function isSplitTx(tx) {
  return splitParts(tx) != null
}

// Distribute `total` across `n` slices at cent precision, returning per-slice
// amounts whose sum equals `total` exactly. The cent-remainder is spread one
// cent at a time over the first `rem` slices, so no slice differs by more
// than 1 cent from any other. E.g. 600 / 28 → 24 × 21.43 + 4 × 21.42.
function distributeCents(total, n) {
  if (total == null) return null
  const cents = Math.round(total * 100)
  const base = Math.trunc(cents / n)
  const rem  = cents - base * n
  // Returns a function that picks the per-slice value for index i.
  return (i) => (base + (i < rem ? 1 : 0)) / 100
}

// Expand a possibly-split tx into one or more display rows. Non-split → [tx].
// Split → N virtual rows. Per-slice amount depends on splitMode:
//   - 'divide' (default, legacy): parent.amount is the TOTAL; slices share
//     it at cent precision via distributeCents (≤1 cent variance).
//   - 'repeat': parent.amount is the per-occurrence amount; each slice
//     gets the full parent.amount, so the implicit total = amount × N.
// Each row also carries:
//   - effectiveDate shifted by sliceIdx * interval (unit-aware)
//   - synthetic id `${parent.id}#${i}` so React keys are unique
//   - _parentId, _sliceIdx (0-based), _sliceTotal — used by the UI to render
//     the "i/N" badge and to resolve clicks back to the parent for edit.
// `date` (payment) stays the same on every slice so per-row FX/time display
// keeps referring to the original payment instant.
export function expandSlices(tx) {
  const parts = splitParts(tx)
  if (!parts) return [tx]
  const { count: n, interval, unit, mode } = parts
  let amtAt, baseAt
  if (mode === 'repeat') {
    // Each slice gets the full per-occurrence amount.
    amtAt = () => tx.amount
    baseAt = tx.amount_base != null ? () => tx.amount_base : null
  } else {
    // 'divide': distribute the parent total across N slices.
    amtAt  = distributeCents(tx.amount, n)
    baseAt = tx.amount_base != null ? distributeCents(tx.amount_base, n) : null
  }
  const out = []
  for (let i = 0; i < n; i++) {
    const isoEffective = addInterval(tx.effectiveDate, i * interval, unit)
    out.push({
      ...tx,
      id: `${tx.id}#${i}`,
      _parentId: tx.id,
      _sliceIdx: i,
      _sliceTotal: n,
      effectiveDate: isoEffective,
      amount: amtAt(i),
      amount_base: baseAt ? baseAt(i) : null,
    })
  }
  return out
}

// Apply expandSlices across a list, returning a flat list. Stable: relative
// ordering is preserved (parent's slices stay contiguous).
export function expandAllSlices(list) {
  if (!Array.isArray(list)) return []
  const out = []
  for (const tx of list) {
    if (isSplitTx(tx)) for (const s of expandSlices(tx)) out.push(s)
    else out.push(tx)
  }
  return out
}
